import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { ListStore } from '@/common/ListStore';
import { PAGINATION_CONFIG } from '@/components/datagrid';
import { type DateRangeTuple, dateRangeToMsBounds } from '@/components/table/common/date_filter';
import { BigIntDevtoolsSerialize } from '@/utils/zustand_helpers';
import { buildMapByKey, patchMapWithUpsert } from '@/utils/zustandListStore';

export interface HistoryFilters<Q> {
  dateRange: DateRangeTuple;
  quickFilter?: Q;
  selectedMarketId?: number;
}

/** 'all' is the quick-filter sentinel for "no filtering". */
export function hasActiveHistoryFilters(filters: HistoryFilters<unknown>): boolean {
  const [from, to] = filters.dateRange;
  return (
    (!!filters.quickFilter && filters.quickFilter !== 'all') || from != null || to != null || filters.selectedMarketId != null
  );
}

export interface HistoryStore<T, Q> extends ListStore<T> {
  items: T[];
  itemsMap: Map<string, T>;

  isHistoryLoading: boolean;
  // True across a whole useHistory fill round; isHistoryLoading drops for a tick
  // between the round's pages, so indicators keyed on it alone would flicker.
  isFilling: boolean;
  queuedItems: T[];

  filters: HistoryFilters<Q>;

  nextPageCursor: string;
  pageSize: number;

  filteredData: T[];

  setQuickFilter: (filter?: Q) => void;
  setDateRangeFilter: (dateRange: DateRangeTuple) => void;
  setMarketFilter: (marketId?: number) => void;

  addPage: (items: T[], nextCursor: string) => void;
  setIsHistoryLoading: (isLoading: boolean) => void;
  setIsFilling: (isFilling: boolean) => void;
}

export function createHistoryStore<T extends { dateTimestamp?: number }, Q extends string | undefined>(options: {
  devtoolsName: string;
  getKey: (item: T) => string;
  applyQuickFilter: (row: T, quick?: Q) => boolean;
  initialPageSize?: number;
  getMarketId?: (item: T) => number | undefined;
  /**
   * Base predicate enforced on every ingest path (addPage, patch, setAll) — rows
   * failing it never enter the store. Pair with useHistory's `sparse` option.
   */
  ingestFilter?: (item: T) => boolean;
}) {
  const { devtoolsName, getKey, applyQuickFilter, getMarketId, ingestFilter } = options;
  const sortByDate = (a: T, b: T) => (b.dateTimestamp ?? 0) - (a.dateTimestamp ?? 0);
  const accept = (items: T[]): T[] => (ingestFilter ? items.filter(ingestFilter) : items);

  const filterRows = (rows: T[], filters: HistoryFilters<Q>): T[] => {
    const { quickFilter, selectedMarketId } = filters;
    const quick = quickFilter && quickFilter !== 'all' ? quickFilter : undefined;
    const bounds = dateRangeToMsBounds(filters.dateRange);
    return rows.filter((row) => {
      if (quick && !applyQuickFilter(row, quick)) return false;
      // Rows without a timestamp are kept — the date filter can't judge them.
      if (bounds && row.dateTimestamp && (row.dateTimestamp < bounds.lo || row.dateTimestamp > bounds.hi)) return false;
      return selectedMarketId == null || !getMarketId || getMarketId(row) === selectedMarketId;
    });
  };

  const makeInitialState = () => ({
    items: [] as T[],
    itemsMap: new Map<string, T>(),
    filters: { dateRange: [null, null], quickFilter: undefined, selectedMarketId: undefined } as HistoryFilters<Q>,
    nextPageCursor: '',
    pageSize: options.initialPageSize ?? PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
    filteredData: [] as T[],
    isHistoryLoading: false,
    isFilling: false,
    queuedItems: [] as T[],
  });

  return create<HistoryStore<T, Q>>()(
    devtools(
      immer((set, get) => {
        const setNamed = (action: string, partial: Partial<HistoryStore<T, Q>>) =>
          set(partial, false, `${devtoolsName}/${action}`);

        const setFilter =
          <K extends keyof HistoryFilters<Q>>(action: string, key: K) =>
          (value: HistoryFilters<Q>[K]) => {
            const s = get();
            const filters = { ...s.filters };
            filters[key] = value;
            setNamed(action, { filters, filteredData: filterRows(s.items, filters) });
          };

        const upsert = (action: string, itemsToUpsert: T[], extra?: Partial<HistoryStore<T, Q>>) => {
          const s = get();
          const map = new Map(s.itemsMap);
          patchMapWithUpsert(map, accept(itemsToUpsert), getKey);
          const items = Array.from(map.values()).sort(sortByDate);
          setNamed(action, { items, itemsMap: map, filteredData: filterRows(items, s.filters), ...extra });
        };

        return {
          ...makeInitialState(),

          setQuickFilter: setFilter('setQuickFilter', 'quickFilter'),
          setDateRangeFilter: setFilter('setDateRangeFilter', 'dateRange'),
          setMarketFilter: setFilter('setMarketFilter', 'selectedMarketId'),
          addPage: (items: T[], nextCursor: string) =>
            upsert('addPage', [...get().queuedItems, ...items], {
              nextPageCursor: nextCursor,
              isHistoryLoading: false,
              queuedItems: [],
            }),
          setAll: (items: T[]) => {
            const accepted = accept(items);
            setNamed('setAll', {
              items: accepted,
              itemsMap: buildMapByKey(accepted, getKey),
              filteredData: filterRows(accepted, get().filters),
            });
          },
          patch: (updates: T[]) => {
            const s = get();
            if (s.isHistoryLoading) setNamed('patch', { queuedItems: [...s.queuedItems, ...updates] });
            else upsert('patch', updates);
          },
          reset: () => setNamed('reset', makeInitialState()),
          setIsHistoryLoading: (isHistoryLoading: boolean) => setNamed('setIsHistoryLoading', { isHistoryLoading }),
          setIsFilling: (isFilling: boolean) => setNamed('setIsFilling', { isFilling }),
        };
      }),
      {
        name: devtoolsName,
        ...BigIntDevtoolsSerialize,
      }
    )
  );
}
